/**
 * Popover menu shown when the user taps a ticket row's ⋮ button or
 * long-presses anywhere on the row. iOS-style rounded panel with a
 * dimmed backdrop that captures outside clicks to close.
 *
 * v1 actions: Pin / Unpin. Designed so future actions (Copy ticket ID,
 * Mark urgent, etc.) can slot in without restructuring.
 */

import { useEffect } from 'react';
import { Pin, PinOff } from 'lucide-react';

export interface TicketRowActionMenuProps {
  isPinned: boolean;
  pinning?: boolean; // disables the pin/unpin item while a mutation is in flight
  onPin: () => void;
  onUnpin: () => void;
  onClose: () => void;
}

export function TicketRowActionMenu({
  isPinned,
  pinning = false,
  onPin,
  onUnpin,
  onClose,
}: TicketRowActionMenuProps) {
  // Close on Escape — pairs with the backdrop's outside-click behavior.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      {/* Backdrop — full-viewport invisible button that swallows the next
       *  outside click and routes it to onClose. role=button + aria-label
       *  for accessibility. */}
      <button
        type="button"
        aria-label="Close menu"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="fixed inset-0 z-40 cursor-default bg-transparent"
      />
      {/* Panel — positioned by the parent (absolute on the row container).
       *  Tap on a row item stops propagation so the backdrop's click doesn't
       *  fire before the action handler. */}
      <div
        role="menu"
        className="absolute right-2 top-full z-50 mt-1 w-48 overflow-hidden rounded-xl border border-border-primary bg-white shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {isPinned ? (
          <button
            type="button"
            role="menuitem"
            disabled={pinning}
            onClick={() => {
              onUnpin();
              onClose();
            }}
            className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-text-primary hover:bg-surface-secondary disabled:opacity-50"
          >
            <PinOff className="h-4 w-4 text-text-secondary" strokeWidth={2} aria-hidden />
            <span>Unpin</span>
          </button>
        ) : (
          <button
            type="button"
            role="menuitem"
            disabled={pinning}
            onClick={() => {
              onPin();
              onClose();
            }}
            className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-text-primary hover:bg-surface-secondary disabled:opacity-50"
          >
            <Pin className="h-4 w-4 text-text-secondary" strokeWidth={2} aria-hidden />
            <span>Pin to top</span>
          </button>
        )}
      </div>
    </>
  );
}
